/**
 * @module AdapterList
 * @package @geenius/adapters/solidjs-ark
 * @description Ark-native searchable SolidJS adapter grid backed by the
 * shared SolidJS adapter provider state.
 */

import { Field } from "@ark-ui/solid/field";
import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import { createAdapters } from "@geenius/adapters/solidjs";
import type { JSX } from "solid-js";
import { createMemo, createSignal, For, Show } from "solid-js";
import { LoadingBridge } from "../bridges";
import { classNames } from "../lib/classNames";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for rendering the Ark Solid adapter list.
 *
 * @property onSelect Optional callback invoked when a domain card is selected.
 * @property filterStatus Optional status filter applied before rendering the grid.
 * @property class Optional class name merged into the outer wrapper.
 */
export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  filterStatus?: AdapterStatusType;
  class?: string;
}

/** Renders a searchable Ark Solid adapter list. */
export function AdapterList(props: AdapterListProps): JSX.Element {
  const ctx = createAdapters();
  const [search, setSearch] = createSignal("");

  const filtered = createMemo(() => {
    let domains = [...ADAPTER_DOMAINS];
    const query = search().toLowerCase();

    if (query) {
      domains = domains.filter((domain) => {
        return (
          (DOMAIN_LABELS[domain] ?? domain).toLowerCase().includes(query) ||
          domain.includes(query) ||
          (ctx.statuses[domain]?.provider?.toLowerCase().includes(query) ??
            false)
        );
      });
    }

    if (props.filterStatus) {
      domains = domains.filter(
        (domain) => ctx.statuses[domain]?.status === props.filterStatus,
      );
    }

    return domains;
  });

  return (
    <Show
      fallback={
        <div
          class={classNames("grid gap-3", props.class)}
          data-gn-adapters-ark-component="adapter-list"
        >
          <LoadingBridge label="Loading adapters" value={64} />
        </div>
      }
      when={!ctx.isLoading}
    >
      <div class={props.class} data-gn-adapters-ark-component="adapter-list">
        <Field.Root class="mb-4">
          <Field.Label class="sr-only">Search adapters</Field.Label>
          <Field.Input
            aria-label="Search adapters"
            class="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20"
            onInput={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search adapters..."
            type="search"
            value={search()}
          />
        </Field.Root>

        <Show
          fallback={
            <div class="rounded-md border border-slate-200 bg-slate-50 p-8 text-center">
              <p class="text-sm font-medium text-slate-500">
                {search()
                  ? "No adapters match your search"
                  : "No adapters configured"}
              </p>
            </div>
          }
          when={filtered().length > 0}
        >
          <ul
            aria-label="Adapter list"
            class="m-0 grid list-none gap-3 p-0 sm:grid-cols-2"
          >
            <For each={filtered()}>
              {(domain) => (
                <li>
                  <AdapterCard
                    domain={domain}
                    onClick={
                      props.onSelect
                        ? () => props.onSelect?.(domain)
                        : undefined
                    }
                    status={ctx.statuses[domain]}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </Show>
  );
}
